// Alertas inteligentes — solo excepciones; cada una llega como push a sus roles.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View , RefreshControl } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Header from '@/components/Header';
import OnaIcon, { type IconName } from '@/components/OnaIcon';
import RoleSheet from '@/components/RoleSheet';
import { api } from '@/api/client';
import { type Alerta, type Sev } from '@/constants/data';
import { refrescarTodo, suscribir, valorActual } from '@/lib/refresco';
import { C, F } from '@/constants/theme';

const SEV_COLOR: Record<Sev, string> = { crit: C.bad, wrn: C.warn, inf: C.aqua, okk: C.good };

function AlertCard({ a, onLeer }: { a: Alerta; onLeer: (claves: string[]) => void }) {
  const [open, setOpen] = useState(false);
  const expandible = !!(a.detalle?.length || a.nota);
  /* Abrir el detalle ES haberla leído: si hay que darle además a un botón,
     nadie lo hace y la campanita se queda encendida igual. */
  const abrir = () => {
    if (expandible) setOpen(!open);
    if (!a.leido && a.claves?.length) onLeer(a.claves);
  };
  return (
    <Pressable style={[s.al, a.leido && s.alLeida]} onPress={abrir}>
      <View style={[s.stripe, { backgroundColor: SEV_COLOR[a.sev], opacity: a.leido ? 0.35 : 1 }]} />
      {!a.leido ? <View style={s.punto} /> : null}
      <View style={s.ic}><OnaIcon name={a.ic as IconName} size={18} color={C.aquaSoft} /></View>
      <View style={{ flex: 1 }}>
        <Text style={s.t}>{a.t}</Text>
        <Text style={s.m}>{a.m}</Text>
        {open && a.detalle?.length ? (
          <View style={s.det}>
            {a.detalle.map(([l, v], i) => (
              <View key={i} style={s.detRow}>
                <Text style={s.detL}>{l}</Text>
                <Text style={s.detV}>{v}</Text>
              </View>
            ))}
            {a.nota ? <Text style={s.detNota}>{a.nota}</Text> : null}
          </View>
        ) : null}
        {expandible ? <Text style={s.hint}>{open ? 'toca para plegar ▴' : 'toca para el detalle ▾'}</Text> : null}
      </View>
      <Text style={s.tm}>{a.tm}</Text>
    </Pressable>
  );
}

export default function AlertasScreen() {
  const [refrescando, setRefrescando] = useState(false);
  const [tiron, setTiron] = useState(valorActual());
  useEffect(() => suscribir(setTiron), []);
  const insets = useSafeAreaInsets();
  const [alertas, setAlertas] = useState<Alerta[]>([]);
  const [source, setSource] = useState<import('@/api/client').Source | undefined>(undefined);
  const [roleSheet, setRoleSheet] = useState(false);

  useEffect(() => { api.alertas().then((r) => { setAlertas(r.data); setSource(r.source); }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tiron]);

  /* Marcar leído: primero se pinta y luego se cuenta al servidor. Si la red
     falla, el aviso vuelve a salir sin leer en el siguiente refresco — que es
     el error correcto: mejor volver a avisar que dar por visto algo que no se
     guardó. `refrescarTodo` es lo que apaga la campanita de la barra. */
  const leer = (claves: string[]) => {
    const nuevas = claves.filter(Boolean);
    if (!nuevas.length) return;
    setAlertas((prev) => prev.map((a) =>
      (a.claves?.length && a.claves.every((c) => nuevas.includes(c)) ? { ...a, leido: true } : a)));
    api.avisosLeer(nuevas).then(() => refrescarTodo()).catch(() => {});
  };
  const sinLeer = alertas.filter((a) => !a.leido && a.claves?.length);

  const day = (d: 'hoy' | 'ayer') => alertas.filter((a) => a.day === d);
  const hoyList = day('hoy');
  const ayerList = day('ayer');

  return (
    <View style={{ flex: 1, backgroundColor: C.bg, paddingTop: insets.top }}>
      <Header onAvatar={() => setRoleSheet(true)} source={source} />
            {/* Tirar hacia abajo para refrescar. Faltaba lo obvio: hasta hoy la
          única forma de traer datos nuevos era cerrar la app y abrirla otra
          vez. Un tirón recarga TODAS las tarjetas a la vez, no solo esta
          pantalla — el contador de lib/refresco.ts lo escuchan todas. */}
      <ScrollView
        refreshControl={<RefreshControl refreshing={refrescando} onRefresh={() => {
          setRefrescando(true); refrescarTodo();
          setTimeout(() => setRefrescando(false), 900);
        }} tintColor={C.aqua} colors={[C.aqua]} progressBackgroundColor={C.bg2} />} contentContainerStyle={{ paddingHorizontal: 18, paddingBottom: 28 }} showsVerticalScrollIndicator={false}>
        <Text style={s.title}>Alertas <Text style={{ color: C.aqua }}>inteligentes</Text></Text>
        <Text style={s.sub}>Solo excepciones — si no pasa nada, Ona Pulse calla</Text>

        {sinLeer.length ? (
          <Pressable style={s.todas} onPress={() => leer(sinLeer.flatMap((a) => a.claves!))}>
            <Text style={s.todasTxt}>
              Marcar {sinLeer.length === 1 ? 'la' : `las ${sinLeer.length}`} como leída{sinLeer.length === 1 ? '' : 's'}
            </Text>
          </Pressable>
        ) : null}

        <Text style={s.dayLbl}>HOY</Text>
        {hoyList.length ? (
          <View style={{ gap: 10 }}>{hoyList.map((a, i) => <AlertCard key={(a.claves?.[0] || a.t) + i} a={a} onLeer={leer} />)}</View>
        ) : (
          <Text style={s.empty}>Sin excepciones ahora mismo. Buen día 🌊</Text>
        )}

        {ayerList.length ? (
          <>
            {/* 🔴 DECÍA «AYER» Y DESDE HOY SERÍA MENTIRA. Mientras Pulse calculaba
                sus avisos al vuelo, todo era de hoy o de ayer. La tabla de
                Hotelos tiene HISTÓRICO —hay comentarios de hace semanas— y
                ponerle «AYER» a algo de hace tres semanas es peor que no
                ponerle nada: el resto de la tarjeta lleva cuánto hace. */}
            <Text style={s.dayLbl}>ANTERIORES</Text>
            <View style={{ gap: 10 }}>{ayerList.map((a, i) => <AlertCard key={(a.claves?.[0] || a.t) + i} a={a} onLeer={leer} />)}</View>
          </>
        ) : null}

        {/* 🔴 EL PIE DECÍA QUE ESTO LLEGA COMO PUSH, Y NO ES VERDAD todavía: las
            notificaciones de verdad están pendientes. Lo que SÍ es cierto desde
            hoy es que cada aviso solo se le enseña a los roles a los que
            afecta, y eso se configura en Sistemas › Avisos. */}
        <Text style={s.foot}>
          Cada aviso se enseña solo a los roles a los que afecta{'\n'}
          Se configura en Hotelos, en tus notificaciones · las push llegarán más adelante
        </Text>
      </ScrollView>
      <RoleSheet visible={roleSheet} onClose={() => setRoleSheet(false)} />
    </View>
  );
}

const s = StyleSheet.create({
  title: { fontFamily: F.displayMed, fontSize: 22, color: C.ink, marginTop: 6 },
  sub: { fontFamily: F.ui, fontSize: 12.5, color: C.muted, marginTop: 2 },
  dayLbl: { fontFamily: F.uiSemi, fontSize: 11, letterSpacing: 1.5, color: C.faint, marginTop: 16, marginBottom: 10 },
  al: {
    flexDirection: 'row', gap: 12, padding: 13, borderRadius: 16,
    backgroundColor: C.card, borderColor: C.line, borderWidth: 1, overflow: 'hidden',
  },
  stripe: { position: 'absolute', left: 0, top: 0, bottom: 0, width: 4 },
  ic: { width: 34, height: 34, borderRadius: 12, backgroundColor: C.bg2, alignItems: 'center', justifyContent: 'center' },
  t: { fontFamily: F.uiSemi, fontSize: 13, color: C.ink, lineHeight: 18 },
  m: { fontFamily: F.ui, fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 16 },
  tm: { fontFamily: F.ui, fontSize: 10, color: C.faint },
  empty: { fontFamily: F.ui, fontSize: 12.5, color: C.muted, paddingVertical: 8 },
  alLeida: { opacity: 0.55 },
  punto: { position: 'absolute', right: 11, top: 11, width: 7, height: 7, borderRadius: 4, backgroundColor: C.aqua },
  todas: {
    alignSelf: 'flex-start', marginTop: 12, paddingHorizontal: 12, paddingVertical: 7,
    borderRadius: 999, borderWidth: 1, borderColor: C.line, backgroundColor: C.bg2,
  },
  todasTxt: { fontFamily: F.uiMed, fontSize: 11.5, color: C.aquaSoft },
  det: { marginTop: 10, borderTopWidth: 1, borderTopColor: C.line, paddingTop: 10, gap: 6 },
  detRow: { flexDirection: 'row', justifyContent: 'space-between', gap: 10 },
  detL: { fontFamily: F.ui, fontSize: 11.5, color: C.muted },
  detV: { fontFamily: F.uiBold, fontSize: 11.5, color: C.ink, fontVariant: ['tabular-nums'], flexShrink: 1, textAlign: 'right' },
  detNota: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 6, lineHeight: 14 },
  hint: { fontFamily: F.ui, fontSize: 9, color: C.faint, marginTop: 7 },
  foot: { fontFamily: F.ui, fontSize: 10, color: C.faint, textAlign: 'center', paddingVertical: 16 },
});
